/**
 * EndActionsSlot
 *
 * Right-aligned slot inside a field's `labelRow` that hosts caller-provided
 * end actions (typically `<FieldActionChips />`). Lives as its own primitive
 * so `StringField`, `NumberField`, and `TextAreaField` share the exact same
 * top-right anchor — same display, alignment, gap, flex-shrink and
 * margin-left across all three. ArrayField and DictionaryField use a
 * different layout (chip lives next to a mode toggle), so they keep their
 * own local style.
 *
 * Pure presentational: no state, no callbacks, no theming hooks beyond
 * Fluent tokens. The wrapping `<span>` is only rendered when `children` is
 * truthy, mirroring the previous `endActions && (...)` guard at every call
 * site.
 */

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
    marginLeft: 'auto',
  },
});

export interface EndActionsSlotProps {
  /** End actions (e.g. FieldActionChips). When falsy, nothing renders. */
  children: ReactNode;
  /** Optional className passthrough for layout overrides. */
  className?: string;
}

export const EndActionsSlot = ({ children, className }: EndActionsSlotProps) => {
  const styles = useStyles();
  if (!children) return null;
  return <span className={mergeClasses(styles.root, className)}>{children}</span>;
};

EndActionsSlot.displayName = 'EndActionsSlot';
